import {
  FORM_SCHEMA_VERSION,
  FormAnswer,
  FormDefinition,
  FormElement,
  FormElementType,
  FormOption
} from '../../types/formSchema';

export interface LegacyFormField {
  _id?: string;
  id?: string;
  heading: string;
  type: 'STRING' | 'NUMBER' | 'ENUM' | 'BOOLEAN' | string;
  isRequired?: boolean;
  isDefault?: boolean;
  enumOptions?: string[];
}

const LEGACY_TO_V2_TYPE: Record<string, FormElementType> = {
  STRING: 'SHORT_TEXT',
  NUMBER: 'NUMBER',
  ENUM: 'SINGLE_CHOICE',
  BOOLEAN: 'YES_NO'
};

const V2_TO_LEGACY_TYPE: Partial<Record<FormElementType, LegacyFormField['type']>> = {
  SHORT_TEXT: 'STRING',
  NUMBER: 'NUMBER',
  SINGLE_CHOICE: 'ENUM',
  YES_NO: 'BOOLEAN'
};

export const normalizeLegacyQuestionTitle = (value: string): string =>
  String(value || '')
    .replace(/^(?:STRING|NUMBER|ENUM|BOOLEAN)\s*[-:]\s*/i, '')
    .trim();

const fnv1a = (value: string): string => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
};

const legacyFieldKey = (field: LegacyFormField): string =>
  JSON.stringify({
    heading: String(field?.heading || '').trim(),
    type: String(field?.type || 'STRING').toUpperCase(),
    enumOptions: Array.isArray(field?.enumOptions) ? field.enumOptions.map(option => String(option).trim()) : []
  });

const stableLegacyId = (field: LegacyFormField, duplicateIndex: number): string => {
  const sourceId = String(field?._id || field?.id || '').trim();
  if (sourceId) return `legacy_${sourceId}`;

  const suffix = duplicateIndex > 0 ? `_${duplicateIndex + 1}` : '';
  return `legacy_${fnv1a(legacyFieldKey(field))}${suffix}`;
};

export const legacyFieldsToFormDefinition = (
  fields: LegacyFormField[],
  metadata: { title?: string; description?: string } = {}
): FormDefinition => {
  const duplicateCounts = new Map<string, number>();

  const elements: FormElement[] = (Array.isArray(fields) ? fields : []).map(field => {
    const key = legacyFieldKey(field);
    const duplicateIndex = duplicateCounts.get(key) || 0;
    duplicateCounts.set(key, duplicateIndex + 1);

    const type = LEGACY_TO_V2_TYPE[String(field.type || 'STRING').toUpperCase()] || 'SHORT_TEXT';
    const id = stableLegacyId(field, duplicateIndex);
    const element: FormElement = {
      id,
      kind: 'QUESTION',
      type,
      title: normalizeLegacyQuestionTitle(field.heading),
      required: !!field.isRequired
    };

    if (type === 'SINGLE_CHOICE') {
      const options: FormOption[] = (field.enumOptions || []).map((option, optionIndex) => {
        const label = String(option);
        return {
          id: `option_${fnv1a(`${id}|${label}|${optionIndex}`)}`,
          label,
          value: label
        };
      });
      element.config = { options, allowOther: false };
    }

    return element;
  });

  return {
    schemaVersion: FORM_SCHEMA_VERSION,
    title: metadata.title || '',
    description: metadata.description || '',
    elements,
    logic: []
  };
};

/**
 * Event messages keep legacy fields next to a v2 definition for backwards compatibility.
 * Some messages created during the migration have the required flag only on that legacy
 * copy. Restore it before rendering so parents cannot skip a mandatory answer.
 */
export const applyLegacyRequiredFlagsToDefinition = (
  definition: FormDefinition,
  fields: LegacyFormField[] = []
): FormDefinition => {
  if (!Array.isArray(fields) || fields.length === 0) return definition;

  let legacyIndex = 0;
  let changed = false;
  const elements = definition.elements.map(element => {
    if (element.kind !== 'QUESTION' || !V2_TO_LEGACY_TYPE[element.type]) return element;

    const legacyField = fields[legacyIndex];
    legacyIndex += 1;
    const legacyRequiredValue: unknown = legacyField?.isRequired;
    const legacyRequired =
      legacyRequiredValue === true ||
      legacyRequiredValue === 1 ||
      String(legacyRequiredValue || '').toLowerCase() === 'true';
    if (!legacyRequired || element.required === true) return element;

    changed = true;
    return { ...element, required: true };
  });

  return changed ? { ...definition, elements } : definition;
};

export const formDefinitionToLegacyFields = (definition: FormDefinition): LegacyFormField[] =>
  (definition?.elements || []).reduce<LegacyFormField[]>((result, element) => {
    if (element.kind !== 'QUESTION') return result;

    const legacyType = V2_TO_LEGACY_TYPE[element.type];
    if (!legacyType) return result;

    const field: LegacyFormField = {
      heading: element.title || '',
      type: legacyType,
      isRequired: !!element.required,
      isDefault: false
    };

    if (legacyType === 'ENUM') {
      field.enumOptions = (element.config?.options || []).map(option => String(option.value || option.label || ''));
    }

    result.push(field);
    return result;
  }, []);

export const legacyFieldsToAnswers = (
  fields: LegacyFormField[],
  definition: FormDefinition = legacyFieldsToFormDefinition(fields)
): FormAnswer[] =>
  definition.elements
    .filter(element => element.kind === 'QUESTION')
    .map((element, index) => ({
      fieldId: element.id,
      value: (fields[index] as any)?.value
    }));

export const applyAnswersToLegacyFields = (
  fields: LegacyFormField[],
  definition: FormDefinition,
  answers: FormAnswer[]
): LegacyFormField[] => {
  const answerByFieldId = new Map(answers.map(answer => [answer.fieldId, answer]));
  const legacyCompatibleTypes = new Set<FormElementType>(['SHORT_TEXT', 'NUMBER', 'SINGLE_CHOICE', 'YES_NO']);
  const compatibleQuestions = definition.elements.filter(
    item => item.kind === 'QUESTION' && legacyCompatibleTypes.has(item.type)
  );
  let legacyIndex = 0;

  return (fields || []).map(field => {
    const element = compatibleQuestions[legacyIndex];
    legacyIndex += 1;
    const answer = element ? answerByFieldId.get(element.id) : undefined;
    let value = answer?.value;

    if (field.type === 'BOOLEAN' && typeof value !== 'boolean' && value !== '' && typeof value !== 'undefined') {
      value = String(value) === 'true';
    }

    return {
      ...field,
      value
    } as LegacyFormField;
  });
};

export const selectFormDefinitionByLegacyFields = (
  definition: FormDefinition,
  fields: LegacyFormField[]
): FormDefinition => {
  const selectedKeys = new Set(
    (fields || []).map(field => {
      const type = LEGACY_TO_V2_TYPE[String(field.type || 'STRING').toUpperCase()] || 'SHORT_TEXT';
      return `${String(field.heading || '')
        .trim()
        .toLowerCase()}|${type}`;
    })
  );
  const includedIds = new Set(
    definition.elements
      .filter(
        element =>
          element.kind !== 'QUESTION' ||
          selectedKeys.has(
            `${String(element.title || '')
              .trim()
              .toLowerCase()}|${element.type}`
          )
      )
      .map(element => element.id)
  );

  let changed = true;
  while (changed) {
    changed = false;
    definition.elements.forEach(element => {
      if (!includedIds.has(element.id)) return;
      (element.visibility?.conditions || []).forEach(condition => {
        if (!includedIds.has(condition.fieldId)) {
          includedIds.add(condition.fieldId);
          changed = true;
        }
      });
    });
    (definition.logic || []).forEach(rule => {
      rule.when.conditions.forEach(condition => {
        if (!includedIds.has(condition.fieldId)) {
          includedIds.add(condition.fieldId);
          changed = true;
        }
      });
    });
  }

  const elements = definition.elements.filter(element => includedIds.has(element.id));
  const elementIds = new Set(elements.map(element => element.id));

  return {
    ...definition,
    elements,
    logic: (definition.logic || []).filter(rule => {
      if (!rule.when.conditions.every(condition => elementIds.has(condition.fieldId))) return false;
      if ('targetId' in rule.action && !elementIds.has(rule.action.targetId)) return false;
      return rule.sourcePageId === '__start__' || elementIds.has(rule.sourcePageId);
    })
  };
};
